import { Trans, t } from '@lingui/macro';
import { EyeOff, MinusCircle } from "lucide-react";
import { useReducer, useState } from "react";
import { useAppUIContext } from "../../context/AppUIContext";
import { Room, Visibility } from "../../models/Room";
import { ResizeImage } from "../../services/Utils/ImageResizer";
import ProfileImg from "./ProfileImg";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

interface IRoomForm { room: Room, onUploadImageSelected: (f: File | null) => void }

const RoomForm: React.FC<IRoomForm> = ({ room, onUploadImageSelected }) => {
  const { workspaces } = useAppUIContext();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [showImageSection, setShowImageSection] = useState(false);
  const [roomProfileImg, setRoomProfileImg] = useState(room.profileImageUrl ?? '');
  const coverImgList = ['falling', 'hello', 'ball', 'moon', 'notebook', 'play', 'sleep', 'roket', 'work', 'coffee'];

  if (workspaces.length === 0)
    return (<div className="p-4">
      <h4 className="text-destructive">Error: The user needs to create a workspace before creating a channel.</h4>
    </div>);
  else
    return (
      <div className="flex flex-col gap-4 p-1">
        <div className="space-y-2">
          <Label className="font-semibold text-muted-foreground"><Trans>Workspace</Trans></Label>
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-md text-sm text-[color:rgb(var(--text-body))]">
            {(() => {
              const ws = workspaces.find(w => w.id === room.workspaceId); return ws?.iconUrl ? (
                <img src={ws.iconUrl} className="w-5 h-5 rounded object-cover shrink-0" alt="" />
              ) : (
                <div className="w-5 h-5 bg-[rgba(255,127,0,0.1)] text-primary border border-[rgba(255,127,0,0.1)] rounded flex items-center justify-center font-bold text-meta-sm shrink-0">
                  {ws?.name?.charAt(0) || "W"}
                </div>
              );
            })()}
            <span className="truncate">{workspaces.find(w => w.id === room.workspaceId)?.name || t`Unknown`}</span>
          </div>
        </div>

        {profileImagePart()}

        <div className="space-y-2">
          <Label htmlFor="room-name" className="font-semibold text-muted-foreground"><Trans>Channel name</Trans></Label>
          <Input
            id="room-name"
            value={room.name}
            onChange={e => { room.name = e.target.value; forceUpdate(); }}
            placeholder={t`Channel name`}
            maxLength={35}
            className="border-border"
          />
        </div>

        <div className="space-y-2">
          <Label className="font-semibold text-muted-foreground"><Trans>Visibility</Trans></Label>
          <RadioGroup
            value={room.visibility}
            onValueChange={(val) => changeVisibility(val as Visibility)}
            className="flex flex-col gap-2"
          >
            <div className="flex items-center space-x-2 p-3 hover:bg-muted transition-colors cursor-pointer" onClick={() => changeVisibility('listed')}>
              <RadioGroupItem value="listed" id="r-listed" />
              <Label htmlFor="r-listed" className="font-normal cursor-pointer flex-1"><Trans>Listed in workspace</Trans></Label>
            </div>
            <div className="flex items-center space-x-2 p-3 hover:bg-muted transition-colors cursor-pointer" onClick={() => changeVisibility('unlisted')}>
              <RadioGroupItem value="unlisted" id="r-unlisted" />
              <Label htmlFor="r-unlisted" className="font-normal cursor-pointer flex items-center gap-1 flex-1">
                <Trans>Unlisted</Trans>
                <EyeOff className="w-4 h-4 text-muted-foreground" />
              </Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="room-desc" className="font-semibold text-muted-foreground"><Trans>Channel description (optional)</Trans></Label>
          <Textarea
            id="room-desc"
            value={room.description}
            onChange={e => { room.description = e.target.value; forceUpdate(); }}
            placeholder={t`Enter a description`}
            maxLength={400}
            className="min-h-[100px] border-border"
          />
        </div>

        <Dialog open={showImageSection} onOpenChange={setShowImageSection}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle><Trans>Select an icon</Trans></DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-5 gap-4 justify-items-center py-4">
              {coverImgList.map(imgName => (
                <div key={imgName} className="hover:scale-110 transition-transform cursor-pointer p-1">
                  {getImageOption(`/assets/img/img-cover-${imgName}-32.svg`)}
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <input id="image-input" type='file' accept="image/*" onChange={coverUploadImgSelected} hidden />
      </div>
    );

  function profileImagePart() {
    return (
      <div className="border rounded-lg p-4 bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-base font-medium"><Trans>Profile image</Trans></Label>
          <Button variant="ghost" size="sm" onClick={() => setShowImageSection(true)}>
            <Trans>Select Icon</Trans>
          </Button>
        </div>
        <div className="flex flex-col items-center justify-center gap-2">
          <div
            onClick={browseFile}
            className="cursor-pointer relative group"
          >
            {roomProfileImg ? (
              <div className="relative">
                <ProfileImg url={roomProfileImg} className="w-24 h-24 rounded-2xl object-cover shadow-sm" />
                <div className="absolute inset-0 bg-on-media group-hover:bg-on-media-hover rounded-2xl transition-colors" />
              </div>
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-muted flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors">
                <span className="text-sm font-medium"><Trans>Upload</Trans></span>
              </div>
            )}
          </div>
          {roomProfileImg && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2"
              onClick={clearImage}
            >
              <MinusCircle className="w-4 h-4 mr-1" />
              <Trans>Remove</Trans>
            </Button>
          )}
        </div>
      </div>
    );
  }

  function clearImage() {
    room.profileImageUrl = null;
    setRoomProfileImg('');
    onUploadImageSelected(null);
  }

  async function coverUploadImgSelected(e: any) {
    if (e.target.files.length) {
      const [_file] = e.target.files;
      const resized = await ResizeImage(_file, 250);
      setRoomProfileImg(URL.createObjectURL(resized))
      onUploadImageSelected(resized);
    }
  }

  function getImageOption(profileImageUrl: string) {
    return <img src={profileImageUrl} className="w-10 h-10 object-contain" onClick={() => {
      room.profileImageUrl = profileImageUrl;
      setRoomProfileImg(profileImageUrl);
      setShowImageSection(false);
    }} />;
  }

  function changeVisibility(visibility: Visibility) {
    room.visibility = visibility;
    forceUpdate();
  }

  function hasWorkspace(workspaceId: string) {
    room.workspaceId = workspaceId
    forceUpdate();
  }
}

export function browseFile() {
  document.getElementById('image-input')?.click();
}

export default RoomForm;